import type { CartonRow } from "./prepackApi";

export interface SealDestinationCartonPreview {
  cartonNumber: number;
  reason: "open_existing" | "new_carton";
  movedLines: Array<{
    sku: string;
    source: "serialized" | "unserialized";
    quantityMoved: number;
  }>;
}

export interface SealCartonPreviewResponse {
  shipmentId: number;
  sealedCartonNumber: number;
  primaryDestinationCartonNumber: number;
  destinationCartons: SealDestinationCartonPreview[];
}

export function cartonHasPendingWork(carton: CartonRow): boolean {
  return carton.items.some((item) => item.pending > 0);
}

/** Devices or bulk units packed above the carton target. */
export function cartonOverTargetCount(carton: CartonRow): number {
  return carton.items.reduce((sum, item) => {
    const planned = item.plannedPhysicalSerialQty ?? item.plannedQty;
    const packed = item.packedPhysicalSerialQty ?? item.packedQty;
    return sum + Math.max(0, packed - planned);
  }, 0);
}

export function showSealCartonButton(carton: CartonRow): boolean {
  return carton.sealed !== true && cartonHasPendingWork(carton);
}

export function formatSealDestinationLine(destination: SealDestinationCartonPreview): string {
  const label =
    destination.reason === "new_carton"
      ? `Carton ${String(destination.cartonNumber)} (new)`
      : `Carton ${String(destination.cartonNumber)}`;
  const parts = destination.movedLines.map((line) => {
    const kind = line.source === "serialized" ? "devices" : "units";
    return `${String(line.quantityMoved)} × ${line.sku} (${kind})`;
  });
  return `${label}: ${parts.join(", ")}`;
}

export function formatSealConfirmCopy(
  sealedCartonNumber: number,
  destinations: readonly SealDestinationCartonPreview[],
): string {
  const lines = destinations.map((destination) => formatSealDestinationLine(destination));
  return `Packed items stay in sealed carton ${String(sealedCartonNumber)}. Remaining work is assigned to the cartons above:\n${lines.join("\n")}`;
}

export function formatSealSuccessToast(
  sealedCartonNumber: number,
  primaryDestinationCartonNumber: number,
  destinations: readonly SealDestinationCartonPreview[],
): string {
  const extra = destinations.filter(
    (destination) => destination.cartonNumber !== primaryDestinationCartonNumber,
  );
  const base = `Carton ${String(sealedCartonNumber)} sealed. Continue on carton ${String(primaryDestinationCartonNumber)}.`;
  if (extra.length === 0) return base;
  const also = extra.map((destination) => String(destination.cartonNumber)).join(", ");
  return `${base} Also assigned to carton ${also}.`;
}

export function sealConfirmPrimaryLabel(primaryDestinationCartonNumber: number): string {
  return `Seal and open carton ${String(primaryDestinationCartonNumber)}`;
}
